import { Ref, ref, watch } from 'vue';

import { AddressDto, Coordinates } from '@tallo/property';

import addressService from '../services/address.service';

/**
 * The autocomplete only knows the street and a loose city line; the lookup answers the rest and
 * the coordinates. `picked` names the address just chosen, or null for one already on record,
 * which needs no lookup.
 */
export function useAddressLookup(picked: () => AddressDto | null): {
  coordinates: Ref<Coordinates | undefined>;
  isLoading: Ref<boolean>;
} {
  const coordinates = ref<Coordinates>();
  const isLoading = ref(false);

  watch(picked, async (location) => {
    if (!location) {
      return;
    }

    isLoading.value = true;

    try {
      const details = await addressService.getAddressDetails(encodeURI(`${location.address}, ${location.city}`));

      // A slower answer for an address picked before this one is dropped
      if (details && picked() === location) {
        coordinates.value = { latitude: details.latitude!, longitude: details.longitude! };
        Object.assign(location, { city: details.city, state: details.state, zip: details.zip });
      }
    } finally {
      // Saving stays blocked until the lookup for the address on screen answers
      if (picked() === location) {
        isLoading.value = false;
      }
    }
  });

  return { coordinates, isLoading };
}
